import * as XLSX from 'xlsx-js-style'
import type { Cell, CellBorder, CellStyle, Sheet, SheetImage, Workbook } from './types'
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT } from './types'
import { colName, ref } from './a1'
import { blankSheet } from './store'
import { createEvaluator } from './engine'

export const ACCEPTED_EXT = ['.xlsx', '.xlsm', '.xlsb', '.xls', '.csv', '.tsv', '.txt', '.json', '.ods']
const IMAGE_META_SHEET = '__AI_SHEET_IMAGES__'
const STYLE_META_SHEET = '__AI_SHEET_STYLES__'
const IMAGE_CHUNK_SIZE = 28_000

export function isAccepted(name: string) {
  const lower = name.toLowerCase()
  return ACCEPTED_EXT.some((e) => lower.endsWith(e))
}

function d2s(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  const base = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
  const hasTime = d.getHours() || d.getMinutes() || d.getSeconds()
  return hasTime ? `${base} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}` : base
}

/** 파일(ArrayBuffer) → 내부 Workbook */
export function parseFile(fileName: string, data: ArrayBuffer): Workbook {
  const lower = fileName.toLowerCase()

  if (lower.endsWith('.json')) {
    const text = new TextDecoder().decode(data)
    const parsed = JSON.parse(text)
    const rows: any[] = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.rows) ? parsed.rows : [parsed]
    const ws = XLSX.utils.json_to_sheet(rows)
    const wbx = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wbx, ws, 'Sheet1')
    return fromXlsx(wbx)
  }

  const wbx = XLSX.read(data, {
    type: 'array',
    cellFormula: true,
    cellDates: true,
    cellNF: true,
    cellStyles: true,
    raw: false,
  })
  return fromXlsx(wbx)
}

function fromXlsx(wbx: XLSX.WorkBook): Workbook {
  const sheets: Sheet[] = []
  const imagesBySheet = readImageMetadata(wbx)
  const stylesBySheet = readStyleMetadata(wbx)

  for (const name of wbx.SheetNames) {
    if (name === IMAGE_META_SHEET || name === STYLE_META_SHEET) continue
    const ws = wbx.Sheets[name]
    const sheet = blankSheet(name.slice(0, 64))
    sheet.images = imagesBySheet.get(name) ?? []
    if (!ws) {
      sheets.push(sheet)
      continue
    }
    const dim = ws['!ref'] ? XLSX.utils.decode_range(ws['!ref']) : null
    sheet.merges = ((ws['!merges'] ?? []) as XLSX.Range[]).map((m) => ({
      r1: m.s.r + 1, c1: m.s.c + 1, r2: m.e.r + 1, c2: m.e.c + 1,
    }))

    for (const key of Object.keys(ws)) {
      if (key.startsWith('!')) continue
      const c = ws[key] as XLSX.CellObject
      const addr = XLSX.utils.decode_cell(key)
      const target = ref(addr.r + 1, addr.c + 1)
      const cell: Cell = {}

      if (c.f) cell.f = '=' + String(c.f)
      else if (c.t === 'n') cell.v = typeof c.v === 'number' ? c.v : Number(c.v ?? 0)
      else if (c.t === 'b') cell.v = Boolean(c.v)
      else if (c.t === 'd') cell.v = c.v instanceof Date ? d2s(c.v) : String(c.v ?? '')
      else if (c.t === 'e') cell.v = String(c.w ?? '#N/A')
      else if (c.v !== undefined && c.v !== null && c.v !== '') cell.v = String(c.v)

      cell.s = stylesBySheet.get(`${name}\u0000${target}`) ?? styleFromXlsx(c)

      if (cell.v !== undefined || cell.f !== undefined || cell.s) sheet.cells[target] = cell
    }

    if (dim) {
      sheet.rows = Math.max(50, Math.min(1_048_576, dim.e.r + 1 + 30))
      sheet.cols = Math.max(12, Math.min(16_384, dim.e.c + 1 + 3))
    }

    const cols = (ws['!cols'] ?? []) as XLSX.ColInfo[]
    cols.forEach((ci, i) => {
      const px = ci?.wpx ?? (ci?.wch ? Math.round(ci.wch * 7.5) : undefined)
      if (px) sheet.colWidths[i + 1] = px
    })
    const rows = (ws['!rows'] ?? []) as XLSX.RowInfo[]
    rows.forEach((ri, i) => {
      const px = ri?.hpx ?? (ri?.hpt ? Math.round(ri.hpt * (96 / 72)) : undefined)
      if (px) sheet.rowHeights[i + 1] = px
    })

    sheets.push(sheet)
  }

  if (!sheets.length) sheets.push(blankSheet('Sheet1'))
  return { sheets, activeSheetId: sheets[0].id }
}

function colorFromXlsx(color: any): string | undefined {
  const rgb = typeof color?.rgb === 'string' ? color.rgb.replace(/^FF/i, '') : ''
  return /^[0-9A-F]{6}$/i.test(rgb) ? `#${rgb}`.toLowerCase() : undefined
}

function borderFromXlsx(side: any): CellBorder | undefined {
  const supported = new Set(['thin', 'medium', 'thick', 'dashed', 'dotted', 'double'])
  if (!supported.has(side?.style)) return undefined
  return { style: side.style, color: colorFromXlsx(side.color) ?? '#000000' }
}

function styleFromXlsx(c: XLSX.CellObject): CellStyle | undefined {
  const raw = (c as any).s ?? {}
  const font = raw.font ?? {}
  const fill = raw.fill ?? (raw.patternType ? raw : {})
  const alignment = raw.alignment ?? {}
  const border = raw.border ?? {}
  const nf = (c as any).z as string | undefined
  const style: CellStyle = {
    bold: font.bold || undefined,
    italic: font.italic || undefined,
    underline: font.underline || undefined,
    color: colorFromXlsx(font.color),
    bg: colorFromXlsx(fill.fgColor),
    align: ['left', 'center', 'right'].includes(alignment.horizontal) ? alignment.horizontal : undefined,
    verticalAlign: alignment.vertical === 'center' ? 'middle'
      : ['top', 'bottom'].includes(alignment.vertical) ? alignment.vertical : undefined,
    wrap: alignment.wrapText || undefined,
    indent: typeof alignment.indent === 'number' ? alignment.indent : undefined,
    textRotation: typeof alignment.textRotation === 'number' ? alignment.textRotation : undefined,
    fontSize: typeof font.sz === 'number' ? font.sz : undefined,
    fontFamily: typeof font.name === 'string' ? font.name : undefined,
    numFmt: nf && nf !== 'General' ? nf : undefined,
    borderTop: borderFromXlsx(border.top),
    borderRight: borderFromXlsx(border.right),
    borderBottom: borderFromXlsx(border.bottom),
    borderLeft: borderFromXlsx(border.left),
  }
  return Object.values(style).some((v) => v !== undefined) ? style : undefined
}

function readStyleMetadata(wbx: XLSX.WorkBook): Map<string, CellStyle> {
  const result = new Map<string, CellStyle>()
  const ws = wbx.Sheets[STYLE_META_SHEET]
  if (!ws) return result
  const rows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, raw: true })
  for (const row of rows.slice(1)) {
    const [sheet, address, json] = row
    if (!sheet || !address || typeof json !== 'string') continue
    try {
      result.set(`${sheet}\u0000${address}`, JSON.parse(json))
    } catch {
      // 손상된 메타데이터 한 행은 무시하고 나머지 통합 문서를 계속 읽는다.
    }
  }
  return result
}

function argb(color?: string): string | undefined {
  const rgb = color?.replace(/^#/, '')
  return rgb && /^[0-9A-F]{6}$/i.test(rgb) ? `FF${rgb.toUpperCase()}` : undefined
}

function borderToXlsx(border?: CellBorder) {
  if (!border) return undefined
  return { style: border.style, color: { rgb: argb(border.color) ?? 'FF000000' } }
}

function styleToXlsx(style: CellStyle): any {
  const out: any = {}
  if (style.bold || style.italic || style.underline || style.color || style.fontSize || style.fontFamily) {
    out.font = {
      bold: style.bold, italic: style.italic, underline: style.underline,
      color: argb(style.color) ? { rgb: argb(style.color) } : undefined,
      sz: style.fontSize, name: style.fontFamily,
    }
  }
  if (style.bg) out.fill = { patternType: 'solid', fgColor: { rgb: argb(style.bg) } }
  if (style.align || style.verticalAlign || style.wrap || style.indent || style.textRotation) {
    out.alignment = {
      horizontal: style.align,
      vertical: style.verticalAlign === 'middle' ? 'center' : style.verticalAlign,
      wrapText: style.wrap,
      indent: style.indent,
      textRotation: style.textRotation,
    }
  }
  const border = {
    top: borderToXlsx(style.borderTop), right: borderToXlsx(style.borderRight),
    bottom: borderToXlsx(style.borderBottom), left: borderToXlsx(style.borderLeft),
  }
  if (Object.values(border).some(Boolean)) out.border = border
  if (style.numFmt) out.numFmt = style.numFmt
  return out
}

function readImageMetadata(wbx: XLSX.WorkBook): Map<string, SheetImage[]> {
  const result = new Map<string, SheetImage[]>()
  const ws = wbx.Sheets[IMAGE_META_SHEET]
  if (!ws) return result
  const rows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, raw: true })
  const grouped = new Map<string, { sheet: string; image: Omit<SheetImage, 'src'>; chunks: string[] }>()
  for (const row of rows.slice(1)) {
    const [sheet, id, name, x, y, width, height, chunkIndex, srcChunk] = row
    if (!sheet || !id || typeof srcChunk !== 'string') continue
    const key = `${sheet}\u0000${id}`
    let entry = grouped.get(key)
    if (!entry) {
      entry = {
        sheet: String(sheet),
        image: {
          id: String(id), name: String(name || 'image'), x: Number(x) || 0, y: Number(y) || 0,
          width: Math.max(40, Number(width) || 240), height: Math.max(40, Number(height) || 160),
        },
        chunks: [],
      }
      grouped.set(key, entry)
    }
    entry.chunks[Math.max(0, Number(chunkIndex) || 0)] = srcChunk
  }
  for (const entry of grouped.values()) {
    const image: SheetImage = { ...entry.image, src: entry.chunks.join('') }
    result.set(entry.sheet, [...(result.get(entry.sheet) ?? []), image])
  }
  return result
}

/** 내부 Workbook → .xlsx Blob (수식 유지) */
export function toXlsxBlob(wb: Workbook): Blob {
  const out = XLSX.utils.book_new()

  for (const sheet of wb.sheets) {
    const ws: XLSX.WorkSheet = {}
    let maxR = 1
    let maxC = 1
    for (const merge of sheet.merges ?? []) {
      maxR = Math.max(maxR, merge.r2)
      maxC = Math.max(maxC, merge.c2)
    }

    for (const [key, cell] of Object.entries(sheet.cells)) {
      const m = /^([A-Z]{1,3})([0-9]+)$/.exec(key)
      if (!m) continue
      const row = Number(m[2])
      let col = 0
      for (let i = 0; i < m[1].length; i++) col = col * 26 + (m[1].charCodeAt(i) - 64)
      maxR = Math.max(maxR, row)
      maxC = Math.max(maxC, col)

      const o: XLSX.CellObject = { t: 's', v: '' }
      if (cell.f) {
        o.f = cell.f.replace(/^=/, '')
        o.t = 'n'
        o.v = 0 // 값은 Excel 이 열 때 재계산
      } else if (typeof cell.v === 'number') {
        o.t = 'n'
        o.v = cell.v
      } else if (typeof cell.v === 'boolean') {
        o.t = 'b'
        o.v = cell.v
      } else if (cell.v === null || cell.v === undefined) {
        if (!cell.s) continue
        o.t = 's'
        o.v = ''
      } else {
        o.t = 's'
        o.v = String(cell.v)
      }
      if (cell.s) {
        ;(o as any).s = styleToXlsx(cell.s)
        if (cell.s.numFmt) (o as any).z = cell.s.numFmt
      }
      ws[key] = o
    }

    ws['!ref'] = `A1:${colName(Math.max(maxC, 1))}${Math.max(maxR, 1)}`
    ws['!cols'] = Array.from({ length: Math.max(maxC, 1) }, (_, i) => ({
      wpx: sheet.colWidths[i + 1] ?? DEFAULT_COL_WIDTH,
    }))
    ws['!rows'] = Array.from({ length: Math.max(maxR, 1) }, (_, i) => ({
      hpx: sheet.rowHeights[i + 1] ?? DEFAULT_ROW_HEIGHT,
    }))
    ws['!merges'] = (sheet.merges ?? []).map((m) => ({
      s: { r: m.r1 - 1, c: m.c1 - 1 },
      e: { r: m.r2 - 1, c: m.c2 - 1 },
    }))
    XLSX.utils.book_append_sheet(out, ws, sheet.name.slice(0, 31))
  }

  const imageRows: any[][] = [['sheet', 'id', 'name', 'x', 'y', 'width', 'height', 'chunk', 'data']]
  for (const sheet of wb.sheets) {
    for (const image of sheet.images ?? []) {
      const chunks = image.src.match(new RegExp(`.{1,${IMAGE_CHUNK_SIZE}}`, 'g')) ?? ['']
      chunks.forEach((chunk, i) =>
        imageRows.push([sheet.name.slice(0, 31), image.id, image.name, image.x, image.y, image.width, image.height, i, chunk])
      )
    }
  }
  if (imageRows.length > 1) {
    XLSX.utils.book_append_sheet(out, XLSX.utils.aoa_to_sheet(imageRows), IMAGE_META_SHEET)
    const index = out.SheetNames.indexOf(IMAGE_META_SHEET)
    out.Workbook = out.Workbook ?? {}
    out.Workbook.Sheets = out.Workbook.Sheets ?? []
    out.Workbook.Sheets[index] = { ...(out.Workbook.Sheets[index] ?? {}), Hidden: 1 }
  }

  const styleRows: any[][] = [['sheet', 'cell', 'style']]
  for (const sheet of wb.sheets) {
    for (const [address, cell] of Object.entries(sheet.cells)) {
      if (cell.s && Object.values(cell.s).some((v) => v !== undefined))
        styleRows.push([sheet.name.slice(0, 31), address, JSON.stringify(cell.s)])
    }
  }
  if (styleRows.length > 1) {
    XLSX.utils.book_append_sheet(out, XLSX.utils.aoa_to_sheet(styleRows), STYLE_META_SHEET)
    const index = out.SheetNames.indexOf(STYLE_META_SHEET)
    out.Workbook = out.Workbook ?? {}
    out.Workbook.Sheets = out.Workbook.Sheets ?? []
    out.Workbook.Sheets[index] = { ...(out.Workbook.Sheets[index] ?? {}), Hidden: 1 }
  }

  const buf = XLSX.write(out, { bookType: 'xlsx', type: 'array', cellStyles: true })
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

/** 활성 시트를 계산된 값 기준 CSV 로 */
export function toCsv(wb: Workbook, sheetId: string): string {
  const sheet = wb.sheets.find((s) => s.id === sheetId) ?? wb.sheets[0]
  const ev = createEvaluator(wb.sheets, sheet.name)
  let maxR = 0
  let maxC = 0
  for (const key of Object.keys(sheet.cells)) {
    const m = /^([A-Z]{1,3})([0-9]+)$/.exec(key)
    if (!m) continue
    let col = 0
    for (let i = 0; i < m[1].length; i++) col = col * 26 + (m[1].charCodeAt(i) - 64)
    maxR = Math.max(maxR, Number(m[2]))
    maxC = Math.max(maxC, col)
  }
  const lines: string[] = []
  for (let r = 1; r <= maxR; r++) {
    const cells: string[] = []
    for (let c = 1; c <= maxC; c++) {
      const t = ev.display(sheet.name, r, c).text
      cells.push(/[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t)
    }
    lines.push(cells.join(','))
  }
  return lines.join('\r\n')
}

export function download(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
